import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil, Workflow, FileUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { api, errMsg, CONTENT_TYPES } from "../lib/api";
import { useRoles } from "../context/RolesContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, Empty, btnPrimary, btnGhost, inputCls } from "../components/ui-kit";
import { RoleBadge, TypeBadge, ClassLinkedBadge } from "../components/Badges";

const MOCK_TEMPLATE = [
  { name: "Write mock", role: "content_writer" },
  { name: "Proofread", role: "proofreader" },
  { name: "Faculty approval", role: "faculty" },
  { name: "Type", role: "typist" },
  { name: "Final proof approval", role: "proofreader" },
  { name: "Upload", role: "uploader" },
];
const BATCH_STEP = { name: "Upload batch-annotated file", role: "faculty" };
const blank = { name: "", content_type: "mocks", class_linked: false, description: "", steps: [{ name: "", role: "content_writer" }] };

const StepEditor = ({ steps, setSteps, classLinked }) => {
  const { roles } = useRoles();
  useEffect(() => {
    if (!roles.length || steps.every((s) => roles.some((r) => r.key === s.role))) return;
    setSteps(steps.map((s) => (roles.some((r) => r.key === s.role) ? s : { ...s, role: roles[0].key })));
  }, [roles, steps, setSteps]);
  const upd = (i, k, v) => setSteps(steps.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const move = (i, d) => { const n = [...steps]; [n[i], n[i + d]] = [n[i + d], n[i]]; setSteps(n); };
  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div key={i} data-testid={`process-step-${i}`} className={`flex items-center gap-2 rounded-lg border p-2 ${i === 0 && classLinked ? "border-purple-500/40 bg-purple-500/5" : "border-[#282E3D]"}`}>
          <span className="w-6 text-center font-mono text-xs text-[#FF6B00]">{i + 1}</span>
          <input required placeholder="Step name" data-testid={`process-step-name-${i}`} className={`${inputCls} py-2`} value={s.name} onChange={(e) => upd(i, "name", e.target.value)} />
          <select data-testid={`process-step-role-${i}`} className={`${inputCls} w-44 py-2`} value={s.role} onChange={(e) => upd(i, "role", e.target.value)}>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          {i === 0 && classLinked && <FileUp className="h-4 w-4 shrink-0 text-purple-300" title="File required" />}
          <button type="button" disabled={i === 0 || (classLinked && i === 1)} onClick={() => move(i, -1)} className="text-slate-500 hover:text-white disabled:opacity-20"><ArrowUp className="h-4 w-4" /></button>
          <button type="button" disabled={i === steps.length - 1 || (classLinked && i === 0)} onClick={() => move(i, 1)} className="text-slate-500 hover:text-white disabled:opacity-20"><ArrowDown className="h-4 w-4" /></button>
          <button type="button" data-testid={`process-step-remove-${i}`} disabled={steps.length === 1 || (classLinked && i === 0)} onClick={() => setSteps(steps.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400 disabled:opacity-20"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="process-builder-add-step-button" onClick={() => setSteps([...steps, { name: "", role: "proofreader" }])} className={`${btnGhost} w-full border-dashed`}><Plus className="h-4 w-4" /> Add step</button>
    </div>
  );
};

const ProcessDialog = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const toggleClass = (v) => {
    const first = f.steps[0];
    const hasBatch = first && first.name === BATCH_STEP.name;
    const steps = v && !hasBatch ? [{ ...BATCH_STEP }, ...f.steps] : !v && hasBatch && f.steps.length > 1 ? f.steps.slice(1) : f.steps;
    setF({ ...f, class_linked: v, steps });
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (f.id) await api.put(`/processes/${f.id}`, f);
      else await api.post("/processes", f);
      toast.success("Process saved");
      onSaved();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#282E3D] bg-[#141721] text-slate-100 sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{f.id ? "Edit process" : "Design a process"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <input required placeholder="Process name, e.g. Mock — Standard" data-testid="process-name-input" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <select data-testid="process-content-type-select" className={inputCls} value={f.content_type} onChange={(e) => setF({ ...f, content_type: e.target.value })}>
              {Object.entries(CONTENT_TYPES).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
            </select>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-[#282E3D] p-4">
            <div>
              <div className="text-sm font-semibold">Class-linked process</div>
              <div className="text-xs text-slate-500">Step 1 becomes "Upload batch-annotated file" and needs a file before it can be completed.</div>
            </div>
            <Switch data-testid="process-class-linked-toggle" checked={f.class_linked} onCheckedChange={toggleClass} className="data-[state=checked]:bg-[#FF6B00]" />
          </label>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Steps, in order</span>
              <button type="button" data-testid="process-use-mock-template" onClick={() => setF({ ...f, steps: f.class_linked ? [{ ...BATCH_STEP }, ...MOCK_TEMPLATE] : MOCK_TEMPLATE })} className="text-xs text-[#FF6B00] hover:underline">Use mock template</button>
            </div>
            <StepEditor steps={f.steps} setSteps={(steps) => setF({ ...f, steps })} classLinked={f.class_linked} />
          </div>
          <textarea rows={2} placeholder="Notes for the team (optional)" data-testid="process-description-input" className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <button data-testid="process-save-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Saving…" : "Save process"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ProcessCard = ({ p, onEdit, onDelete, canEdit }) => (
  <Panel data-testid={`process-card-${p.id}`} className="p-6">
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="flex flex-wrap gap-2"><TypeBadge type={p.content_type} />{p.class_linked && <ClassLinkedBadge />}</div>
        <div className="mt-3 font-heading text-xl font-bold">{p.name}</div>
        {p.description && <p className="mt-1 text-sm text-slate-500">{p.description}</p>}
      </div>
      {canEdit && (
        <div className="flex gap-2">
          <button data-testid={`process-edit-${p.id}`} onClick={onEdit} className="rounded-lg p-2 text-slate-500 hover:bg-[#1C202E] hover:text-white"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`process-delete-${p.id}`} onClick={onDelete} className="rounded-lg p-2 text-slate-500 hover:bg-[#1C202E] hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </div>
    <ol className="mt-6 flex gap-2 overflow-x-auto pb-1">
      {p.steps.map((s, i) => (
        <li key={s.id} className="min-w-[150px] rounded-lg border border-[#282E3D] bg-[#0B0D12] p-3">
          <div className="font-mono text-[10px] text-[#FF6B00]">STEP {i + 1}{s.requires_file && " · FILE"}</div>
          <div className="mt-1 truncate text-sm font-medium">{s.name}</div>
          <div className="mt-2"><RoleBadge role={s.role} /></div>
        </li>
      ))}
    </ol>
  </Panel>
);

export default function Processes() {
  const { user } = useAuth();
  const canEdit = user.is_admin;
  const [procs, setProcs] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/processes").then((r) => setProcs(r.data));
  useEffect(() => { load(); }, []);
  const del = async (p) => {
    if (!window.confirm(`Archive process "${p.name}"? Existing work items keep running.`)) return;
    await api.delete(`/processes/${p.id}`);
    toast.success("Process archived");
    load();
  };
  return (
    <div>
      <PageHeader eyebrow={canEdit ? "Process designer" : "Processes · view only"} title="Processes" desc={canEdit ? "Define the ordered steps each content type goes through and which role owns each step." : "The steps each content type goes through. Only the super admin can change them."}
        action={canEdit && <button data-testid="open-process-designer-button" className={btnPrimary} onClick={() => setEditing(blank)}><Plus className="h-4 w-4" /> New process</button>} />
      {!procs ? <div className="text-slate-500">Loading…</div> : !procs.length ? (
        <Empty icon={Workflow} title="No processes yet" desc={canEdit ? "Design your first process, e.g. the 6-step mock pipeline." : "The super admin hasn't designed any processes yet."} testId="processes-empty" />
      ) : (
        <div className="grid gap-4">{procs.map((p) => <ProcessCard key={p.id} p={p} canEdit={canEdit} onEdit={() => setEditing({ ...p, steps: p.steps.map(({ name, role }) => ({ name, role })) })} onDelete={() => del(p)} />)}</div>
      )}
      {canEdit && editing && <ProcessDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
